/**
 * MCP Connection Form (Refactored)
 * Main component that orchestrates MCP connection flow
 * 
 * MCP Protocol Flow:
 * 1. AccountsGenie backend acts as MCP Client
 * 2. Backend connects to MCP Server using specified transport
 * 3. Backend handles initialize/initialized handshake
 * 4. Backend stores any auth tokens from server
 * 5. Frontend just triggers connection and shows status
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Loader2, 
  Link2,
  Server,
  AlertCircle
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../hooks/use-toast';
import { apiClient } from '../../services/api';
import { useOAuthHandler } from './OAuthHandler';
import { ConnectionStatusDisplay } from './ConnectionStatus';
import ApiKeyModal from './ApiKeyModal';
import QRCodeModal from './QRCodeModal';

interface MCPServerInfo {
  id: string;
  mcp_server_name: string;
  server_url: string;
  transport_type: string;
  metadata?: any;
  icon?: string;
}

interface MCPConnectionFormProps {
  mcpServerId: string;
  serverInfo: MCPServerInfo;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface MCPConnectionStatus {
  connected: boolean;
  initialized: boolean;
  capabilities?: {
    tools?: string[];
    resources?: string[];
    prompts?: string[];
  };
  error?: string;
  lastConnected?: string;
}

const MCPConnectionForm: React.FC<MCPConnectionFormProps> = ({
  mcpServerId,
  serverInfo,
  onSuccess,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<MCPConnectionStatus | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [authData, setAuthData] = useState<any>(null);
  const { user } = useAuthStore();
  const { toast } = useToast();

  useEffect(() => {
    checkConnectionStatus();
  }, [mcpServerId]);

  const checkConnectionStatus = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const response = await apiClient.get('/api/mcp/status', {
        params: {
          tenant_id: user.id,
          mcp_server_id: mcpServerId
        }
      });
      
      if (response.data?.success) {
        const status = response.data.data || { connected: false, initialized: false };
        // Don't show error for initial "not connected" state
        if (status.error === "Not connected") {
          delete status.error;
        }
        setConnectionStatus(status);
      } else {
        setConnectionStatus({ connected: false, initialized: false });
      }
    } catch (error) {
      console.error('Failed to check MCP connection status:', error);
      setConnectionStatus({ connected: false, initialized: false });
    } finally {
      setLoading(false);
    }
  };

  const { handleOAuth } = useOAuthHandler({
    user,
    mcpServerId,
    serverName: serverInfo.mcp_server_name,
    onSuccess: () => {
      setConnectionStatus({ connected: true, initialized: true });
      onSuccess?.();
    },
    onError: (error) => {
      toast({
        title: 'Authentication Error',
        description: error,
        variant: 'destructive'
      });
    },
    setConnecting,
    checkConnectionStatus
  });

  const handleConnect = async () => {
    if (!user?.id) return;
    
    try {
      setConnecting(true);
      
      // Initialize MCP connection - backend handles the protocol
      const response = await apiClient.post('/api/mcp/connect', {
        tenant_id: user.id,
        mcp_server_id: mcpServerId,
        redirect_uri: `${window.location.origin}/auth/callback`
      });
      
      const data = response.data;
      
      if (data.success) {
        setConnectionStatus(data.data);
        
        // Check if already connected
        if (data.data.connected && !data.data.auth_required) {
          toast({
            title: 'Connected',
            description: `Successfully connected to ${serverInfo.mcp_server_name}`
          });
          onSuccess?.();
          return;
        }
        
        // Handle authentication requirements
        if (data.data.auth_required) {
          const authType = data.data.auth_type || (data.data.auth_url ? 'oauth2' : undefined);
          
          switch(authType) {
            case 'oauth2':
              // Handle OAuth2 authentication
              if (data.data.auth_url) {
                await handleOAuth(data.data.auth_url);
              } else {
                throw new Error('No auth URL received from connect response');
              }
              break;
              
            case 'api_key':
              setAuthData(data.data);
              setShowApiKeyModal(true);
              break;
              
            case 'qr_code':
              // Open QR code directly in popup window (better UX for scanning)
              if (data.data.qr_data) {
                window.open(data.data.qr_data, '_blank', 'width=400,height=500,resizable=yes,scrollbars=yes');
                // Start polling for connection status
                pollForConnectionStatus();
              } else {
                setAuthData(data.data);
                setShowQRModal(true);
              }
              break;
              
            default:
              toast({
                title: 'Authentication Required',
                description: `Authentication type: ${authType}`,
                variant: 'destructive'
              });
          }
        } else {
          onSuccess?.();
        }
      } else {
        throw new Error(data.message || 'Failed to initialize MCP connection');
      }
    } catch (error: any) {
      console.error('MCP connection error:', error?.message || error);
      
      const errorMessage = getErrorMessage(error);
      
      toast({
        title: 'Connection Failed',
        description: errorMessage,
        variant: 'destructive'
      });
      setConnectionStatus({ 
        connected: false, 
        initialized: false, 
        error: errorMessage 
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      
      const response = await apiClient.post('/api/mcp/disconnect', {
        tenant_id: user.id,
        mcp_server_id: mcpServerId
      });
      
      const data = response.data;
      
      if (data.success) {
        toast({
          title: 'Disconnected',
          description: `Disconnected from ${serverInfo.mcp_server_name}`
        });
        setConnectionStatus({ connected: false, initialized: false });
      } else {
        throw new Error(data.message || 'Failed to disconnect');
      }
    } catch (error: any) {
      console.error('Failed to disconnect:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to disconnect from MCP server',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (error: any): string => {
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.response?.data?.error) return error.response.data.error;
    if (error?.message) return error.message;
    if (error?.name === 'APIError' && error?.toString) return error.toString();
    if (typeof error === 'string') return error;
    if (error?.response?.statusText) return `HTTP ${error.response.status}: ${error.response.statusText}`;
    return 'Failed to connect to MCP server';
  };

  const handleApiKeySubmit = async (credentials: Record<string, string>) => {
    if (!user?.id) return;
    
    try {
      const response = await apiClient.post('/api/mcp/auth/store', {
        tenant_id: user.id,
        mcp_server_id: mcpServerId,
        credentials: credentials
      });
      
      if (response.data?.success) {
        toast({
          title: 'Authentication Saved',
          description: 'Your credentials have been securely stored'
        });
        await checkConnectionStatus();
      }
    } catch (error) {
      console.error('Failed to save auth credentials:', error);
      toast({
        title: 'Failed to Save Credentials',
        description: 'Please try authenticating again',
        variant: 'destructive'
      });
    }
    
    setShowApiKeyModal(false);
    setAuthData(null);
  };

  const handleQRSuccess = () => {
    setShowQRModal(false);
    setAuthData(null);
    checkConnectionStatus();
    onSuccess?.();
  };

  const pollForConnectionStatus = () => {
    const pollInterval = setInterval(async () => {
      const isConnected = await pollQRStatus();
      if (isConnected) {
        clearInterval(pollInterval);
        checkConnectionStatus();
        onSuccess?.();
      }
    }, 3000);
    setTimeout(() => clearInterval(pollInterval), 180000);
  };

  const pollQRStatus = async (): Promise<boolean> => {
    if (!user?.id) return false;
    
    try {
      // Use WhatsApp-specific polling endpoint for WhatsApp servers
      const endpoint = serverInfo.mcp_server_name.toLowerCase().includes('whatsapp') 
        ? '/api/mcp/auth/whatsapp/poll'
        : '/api/mcp/auth/poll';
        
      const response = await apiClient.get(endpoint, {
        params: {
          tenant_id: user.id,
          mcp_server_id: mcpServerId
        }
      });
      
      // Check if connection is established (oauth_complete or connected status)
      return response.data?.success && (
        response.data?.data?.authenticated || 
        response.data?.data?.connected ||
        response.data?.data?.status === 'oauth_complete' ||
        response.data?.data?.status === 'connected'
      );
    } catch (error) {
      console.error('Failed to poll QR status:', error);
      return false;
    }
  };

  // Use the ConnectionStatusDisplay component for status rendering
  const statusDisplay = (
    <ConnectionStatusDisplay
      connectionStatus={connectionStatus}
      serverInfo={serverInfo}
      loading={loading}
      connecting={connecting}
      onConnect={handleConnect}
      onDisconnect={handleDisconnect}
      onCancel={onCancel}
    />
  );

  // Only return statusDisplay if ConnectionStatusDisplay has content to show
  if (connectionStatus?.connected || connectionStatus?.error || loading) {
    return statusDisplay;
  }

  // Show connect prompt (default state)
  return (
    <>
      <div className="space-y-4">
        <Alert>
          <Server className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p>Connect to {serverInfo.mcp_server_name} using MCP protocol.</p>
              <div className="text-xs space-y-1">
                <p>• Server URL: {serverInfo.server_url}</p>
                <p>• Transport: {serverInfo.transport_type}</p>
                {serverInfo.metadata?.description && (
                  <p>• {serverInfo.metadata.description}</p>
                )}
              </div>
            </div>
          </AlertDescription>
        </Alert>
        
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            The MCP server will handle its own authentication if required.
            AccountsGenie will securely store any credentials provided by the server.
          </AlertDescription>
        </Alert>
        
        <div className="flex gap-2">
          <Button onClick={handleConnect} disabled={connecting}>
            {connecting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Initializing MCP Connection...
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-2" />
                Connect to {serverInfo.mcp_server_name}
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={connecting}
          >
            Cancel
          </Button>
        </div>
      </div>

      {/* API Key Modal */}
      {showApiKeyModal && (
        <ApiKeyModal
          isOpen={showApiKeyModal}
          onClose={() => {
            setShowApiKeyModal(false);
            setAuthData(null);
          }}
          onSubmit={handleApiKeySubmit}
          serverName={serverInfo.mcp_server_name}
          requiredFields={authData?.required_fields || ['api_key']}
          description={authData?.description}
        />
      )}

      {/* QR Code Modal */}
      {showQRModal && (
        <QRCodeModal
          isOpen={showQRModal}
          onClose={() => {
            setShowQRModal(false);
            setAuthData(null);
          }}
          onSuccess={handleQRSuccess}
          serverName={serverInfo.mcp_server_name}
          qrData={authData?.qr_data || ''}
          sessionId={authData?.session_id}
          pollStatusFunc={pollQRStatus}
          description={authData?.description}
        />
      )}
    </>
  );
};

export default MCPConnectionForm;